import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase/client';
import { ladeEigentuemerIds, mitVerknuepftem } from '../../../lib/tickets/ticketEigentuemer';
import { Buildings, ChatCircleText, EnvelopeSimpleOpen, Globe, PaperPlaneTilt, SpinnerGap, User } from '@phosphor-icons/react';
import { Segment } from '../../../components/ui/Segment';
import { zeigeWarteAufMieter } from '../../../lib/tickets/darstellung';
import ListTextarea from '../../../components/ui/ListTextarea';
import KiTextHelfer from '../../../components/ki/KiTextHelfer';
import EmailVerfassen from '../emails/EmailVerfassen';
import { useIslandActions } from '../../../components/island/IslandProvider';
import { verknuepfeTicketMitEmail } from '../../../lib/ki/ticketLink';
import { mieterKandidaten, eigentuemerKandidaten, grundText, type EmpfaengerKandidat, type NachrichtTyp } from '../../../lib/tickets/empfaenger';
import type { Ticket } from './types';
import { istWohnend } from '../../../lib/dashboard/leerstand';
import { MAIL_FORM_META } from '../../../components/island/form/mailMeta';

export interface TicketComposerProps {
  ticket: Ticket;                 // braucht mieter_rel/mieter_id für "Nachricht an Mieter"
  userProfile?: any;
  // Nach erfolgreichem Senden (interner Kommentar ODER gespiegelte Nachricht) — Parent
  // bündelt hier Thread-Remount + Mini-Timeline-Refresh + Refetch, siehe task-4-report.md.
  onSent: () => void;
  /** „Danach ‚Warte auf Mieter‘“: Status nach dem Versand an den Mieter setzen (Handoff §5) */
  onWarteAufMieter?: () => Promise<void>;
}

// #564 T3: 'portal' = Antwort im Mieter-/Eigentümerportal (keine Mail)
type Ziel = 'intern' | NachrichtTyp | 'portal';
type PortalRolle = 'mieter' | 'eigentuemer';
const PORTAL_TEXT: Record<PortalRolle, string> = { mieter: 'Mieter', eigentuemer: 'Eigentümer' };
/** Empfänger einer Portal-Antwort: eine Person (nur sie sieht die Antwort, #564 T3-Korrektur) */
interface PortalPerson { profil: string; rolle: PortalRolle; name: string }

export default function TicketComposer({ ticket, userProfile, onSent, onWarteAufMieter }: TicketComposerProps) {
  const { openForm, closeForm } = useIslandActions();

  // Interner Kommentar (einziges Inline-Eingabefeld — Mieter-/Eigentümer-Nachricht öffnen
  // das volle EmailVerfassen in der Pille, siehe handleNachrichtOeffnen)
  const [kommentarText, setKommentarText] = useState('');
  const [kommentarSenden, setKommentarSenden] = useState(false);
  // Kandidatenlisten statt Einzel-Adresse — mehrere Mieter/Eigentümer am Objekt sind der
  // Normalfall (siehe docs/superpowers/specs/2026-08-05-ticket-empfaenger-auswahl-design.md).
  const [mieterListe, setMieterListe] = useState<EmpfaengerKandidat[]>([]);
  const [mieterGesamt, setMieterGesamt] = useState(0);
  const [eigentuemerListe, setEigentuemerListe] = useState<EmpfaengerKandidat[]>([]);
  const [eigentuemerGesamt, setEigentuemerGesamt] = useState(0);
  const [mailLaedt, setMailLaedt] = useState<NachrichtTyp | null>(null);
  // Antwortfeld (Handoff §5): Ziel-Segment, gewählter Empfänger je Typ, „Danach Warte auf Mieter“
  const [ziel, setZiel] = useState<Ziel>('intern');
  const [empfaengerId, setEmpfaengerId] = useState<Record<NachrichtTyp, string | null>>({ Mieter: null, 'Eigentümer': null });
  const [danachWarte, setDanachWarte] = useState(true);
  // #564 T3: An wen kann im Portal geantwortet werden? Mieter = Verfasser der Portal-Meldung/-Anfrage;
  // Eigentümer = wer aus dem Eigentümerportal geschrieben hat, sonst der am Ticket eingetragene Eigentümer mit Zugang.
  const [portalPersonen, setPortalPersonen] = useState<PortalPerson[]>([]);
  const [portalAnProfil, setPortalAnProfil] = useState<string | null>(null);
  useEffect(() => {
    let aktiv = true;
    void (async () => {
      const { data: t } = await supabase.from('tickets')
        .select('quelle, erstellt_von, eigentuemer_id, fuer_eigentuemer_sichtbar, ist_rechtlich_sensibel').eq('id', ticket.id).maybeSingle();
      if (!t) return;
      const kand: { profil: string; rolle: PortalRolle }[] = [];
      if (t.quelle === 'mieterportal' && t.erstellt_von) kand.push({ profil: t.erstellt_von, rolle: 'mieter' });
      if (t.fuer_eigentuemer_sichtbar && !t.ist_rechtlich_sensibel) {
        const { data: schreiber } = await supabase.from('ticket_kommentare').select('autor_id').eq('ticket_id', ticket.id).eq('portal_von', 'eigentuemer');
        for (const k of schreiber ?? []) if (k.autor_id) kand.push({ profil: k.autor_id, rolle: 'eigentuemer' });
        if (t.eigentuemer_id) {
          const { data: e } = await supabase.from('eigentuemer').select('profile_id').eq('id', t.eigentuemer_id).maybeSingle();
          if (e?.profile_id) kand.push({ profil: e.profile_id, rolle: 'eigentuemer' });
        }
      }
      const eindeutig = kand.filter((k, i) => kand.findIndex((x) => x.profil === k.profil) === i);
      const { data: namen } = eindeutig.length
        ? await supabase.from('profiles').select('id, full_name').in('id', eindeutig.map((k) => k.profil))
        : { data: [] as { id: string; full_name: string | null }[] };
      if (!aktiv) return;
      const personen = eindeutig.map((k) => ({ ...k, name: namen?.find((n) => n.id === k.profil)?.full_name || PORTAL_TEXT[k.rolle] }));
      setPortalPersonen(personen);
      setPortalAnProfil(personen[0]?.profil ?? null);
    })();
    return () => { aktiv = false; };
  }, [ticket.id]);
  const portalPerson = portalPersonen.find((p) => p.profil === portalAnProfil) ?? null;

  // Composer-Felder beim Ticketwechsel zurücksetzen.
  useEffect(() => {
    setKommentarText('');
    setZiel('intern');
    setDanachWarte(true);
  }, [ticket.id]);

  // Mieter-Kandidaten: der verknüpfte Mieter, sonst alle Mieter der Einheit bzw. des Objekts.
  // Eigener Effect, damit ein Inline-Edit des Mieters am offenen Ticket den Button nicht
  // stale lässt (Review-Fund aus Task 4).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const spalten = 'id, vorname, nachname, firma_name, email, status';
      let rows: any[] = [];
      if (ticket.mieter_id) {
        const { data } = await supabase.from('mieter').select(spalten).eq('id', ticket.mieter_id);
        rows = data || [];
      } else if (ticket.einheit_id) {
        const { data } = await supabase.from('mieter').select(spalten).eq('einheit_id', ticket.einheit_id);
        // Nur noch wohnende Mieter: Beim Auszug bleibt objekt_id/einheit_id teils stehen,
        // nur `status` kippt (373 inaktive Datensaetze) — sonst schreibt man Ex-Mietern.
        rows = (data || []).filter((m: any) => istWohnend(m.status));
      } else if (ticket.objekt_id) {
        const { data } = await supabase.from('mieter').select(spalten).eq('objekt_id', ticket.objekt_id);
        rows = (data || []).filter((m: any) => istWohnend(m.status));
      }
      if (cancelled) return;
      setMieterGesamt(rows.length);
      setMieterListe(mieterKandidaten(rows));
    })();
    return () => { cancelled = true; };
  }, [ticket.mieter_id, ticket.einheit_id, ticket.objekt_id]);

  // Eigentümer-Kandidaten: der am Ticket verknüpfte Eigentümer (#513) zuerst, dann die laufenden
  // Eigentümer der Einheit, sonst des Objekts, sonst (WEG) aller Einheiten des Objekts.
  // Kein `.maybeSingle()` – mehrere Zeilen sind der Normalfall, nicht der Fehlerfall.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const eigIds = mitVerknuepftem(ticket.eigentuemer_id, await ladeEigentuemerIds(ticket.objekt_id, ticket.einheit_id));
      if (eigIds.length === 0) {
        if (!cancelled) { setEigentuemerGesamt(0); setEigentuemerListe([]); }
        return;
      }
      const { data: rows } = await supabase
        .from('eigentuemer').select('id, anrede, vorname, nachname, firma_name, email').in('id', eigIds);
      if (cancelled) return;
      setEigentuemerGesamt((rows || []).length);
      const kandidaten = eigentuemerKandidaten(rows || []);
      // verknüpfter Eigentümer oben (wird damit als Empfänger vorgeschlagen)
      const v = ticket.eigentuemer_id;
      setEigentuemerListe(v ? [...kandidaten.filter(k => k.id === v), ...kandidaten.filter(k => k.id !== v)] : kandidaten);
    })();
    return () => { cancelled = true; };
  }, [ticket.einheit_id, ticket.objekt_id, ticket.eigentuemer_id]);

  // Interner Kommentar — exakt das Insert-Muster aus TicketKommentare.handleSend
  // (autor_id/inhalt/ist_intern), ohne ticket_historie-Eintrag (hier nicht nötig).
  const handleKommentarSenden = async () => {
    if (!kommentarText.trim() || kommentarSenden) return;
    setKommentarSenden(true);
    try {
      await supabase.from('ticket_kommentare').insert({
        ticket_id: ticket.id,
        autor_id: userProfile?.id,
        inhalt: kommentarText.trim(),
        ist_intern: true,
        parent_id: null,
      });
      setKommentarText('');
      onSent();
    } catch (err) {
      console.error('Error sending internal comment:', err);
    } finally {
      setKommentarSenden(false);
    }
  };

  // #564 T3: Antwort im Portal – Kommentar mit portal_an (ist_intern, damit Hausmeister ihn nicht sehen). Keine Mail.
  const handlePortalAntwort = async () => {
    if (!kommentarText.trim() || kommentarSenden || !portalPerson) return;
    setKommentarSenden(true);
    try {
      const { error } = await supabase.from('ticket_kommentare').insert({
        ticket_id: ticket.id, autor_id: userProfile?.id, inhalt: kommentarText.trim(), ist_intern: true, parent_id: null,
        portal_an: portalPerson.rolle, portal_an_profil: portalPerson.profil,
      });
      if (error) throw error;
      await supabase.from('ticket_historie').insert({
        ticket_id: ticket.id, benutzer_id: userProfile?.id, aktion: 'kommentar',
        neuer_wert: `Im ${portalPerson.rolle === 'mieter' ? 'Mieterportal' : 'Eigentümerportal'} an ${portalPerson.name} geantwortet: ${kommentarText.trim().slice(0, 200)}`,
      });
      setKommentarText('');
      onSent();
    } catch (err) {
      console.error('Antwort im Portal fehlgeschlagen:', err);
    } finally {
      setKommentarSenden(false);
    }
  };

  // Nachricht an Mieter/Eigentümer: öffnet DIREKT das volle EmailVerfassen in der Pille
  // (Vorlagen, Briefpapier, Signatur, Anhänge inkl. Drive-Picker im Objekt-Ordner) —
  // wie "Neue E-Mail" im E-Mail-Modul. Empfänger + Betreff vorbefüllt.
  const handleNachrichtOeffnen = async (typ: NachrichtTyp, empfaenger: string, vorText = '', warteSetzen = false) => {
    if (!empfaenger || mailLaedt) return;
    setMailLaedt(typ);
    try {
      const { data: konto, error } = await supabase
        .from('email_konten').select('id, email_adresse, bezeichnung').eq('aktiv', true).limit(1).maybeSingle();
      if (error) throw error;
      if (!konto) { alert('Kein E-Mail-Konto konfiguriert.'); return; }

      // Drive-Picker im Objekt-Ordner des Tickets starten (falls vorhanden).
      let aktenStartOrdner: { id: string; name: string } | undefined;
      if (ticket.objekt_id) {
        const { data: obj } = await supabase
          .from('objekte').select('gdrive_folder_id, bezeichnung').eq('id', ticket.objekt_id).maybeSingle();
        if (obj?.gdrive_folder_id) {
          aktenStartOrdner = { id: obj.gdrive_folder_id, name: obj.bezeichnung || 'Objekt' };
        }
      }

      openForm(
        <EmailVerfassen
          variant="pille"
          konto={{ id: konto.id, email_adresse: konto.email_adresse, bezeichnung: konto.bezeichnung || konto.email_adresse }}
          draft={{
            an: [empfaenger],
            betreff: `AW: ${ticket.titel} (${ticket.ticket_nummer})`,
            text_inhalt: '',
          }}
          aktenStartOrdner={aktenStartOrdner}
          // Getippter Text aus dem Antwortfeld – über autoText (kein Doppel-Rendern im Briefbogen)
          autoText={vorText || undefined}
          onClose={() => closeForm()}
          onSent={(info) => {
            closeForm();
            if (vorText) setKommentarText('');
            void handleNachrichtGesendet(typ, info, warteSetzen);
          }}
        />,
        `Nachricht an ${typ}`,
        640, MAIL_FORM_META,
      );
    } catch (e) {
      console.error(e);
      alert('E-Mail-Konto konnte nicht geladen werden.');
    } finally {
      setMailLaedt(null);
    }
  };

  // Nach Versand: gesendete Mail ans Ticket verknüpfen (erscheint als Thread-Eintrag mit
  // Briefpapier + Anhängen) + Historie. Volltext-Spiegel-Kommentar nur als Fallback,
  // wenn keine emailId zurückkam.
  const handleNachrichtGesendet = async (typ: NachrichtTyp, info?: { betreff: string; text: string; anhaenge?: { name: string }[]; emailId?: string | null }, warteSetzen = false) => {
    const text = info?.text?.trim() || '';
    const aktion = typ === 'Mieter' ? 'nachricht_mieter' : 'nachricht_eigentuemer';
    try {
      let emailVerknuepft = false;
      if (info?.emailId) {
        try {
          await verknuepfeTicketMitEmail(ticket.id, info.emailId);
          emailVerknuepft = true;
        } catch (e) { console.warn('Gesendete Mail konnte nicht verknüpft werden', e); }
      }
      if (!emailVerknuepft) {
        const anhangZeile = info?.anhaenge?.length
          ? `\n\nAnhänge: ${info.anhaenge.map((a) => a.name).join(', ')}`
          : '';
        await supabase.from('ticket_kommentare').insert({
          ticket_id: ticket.id,
          autor_id: userProfile?.id,
          inhalt: (text ? `Nachricht an ${typ} gesendet:\n\n${text}` : `Nachricht an ${typ} gesendet.`) + anhangZeile,
          ist_intern: true,
          parent_id: null,
        });
      }
      await supabase.from('ticket_historie').insert({
        ticket_id: ticket.id,
        benutzer_id: userProfile?.id,
        aktion,
        neuer_wert: `Nachricht an ${typ} gesendet: ${(text || info?.betreff || '').slice(0, 200)}`,
      });
    } catch (e) {
      console.warn(`Ticket-Nachbereitung nach ${typ}-Nachricht fehlgeschlagen`, e);
    }
    if (warteSetzen && onWarteAufMieter) {
      try { await onWarteAufMieter(); } catch (e) { console.warn('Status „Warte auf Mieter“ konnte nicht gesetzt werden', e); }
    }
    onSent();
  };

  // ── Darstellung (Handoff §5) ─────────────────────────────────────────────────────────────
  const listeVon = (typ: NachrichtTyp) => (typ === 'Mieter' ? mieterListe : eigentuemerListe);
  const gewaehlt = (typ: NachrichtTyp): EmpfaengerKandidat | null => {
    const liste = listeVon(typ);
    return liste.find(k => k.id === empfaengerId[typ]) ?? liste[0] ?? null;
  };
  const warteMoeglich = ziel === 'Mieter' && zeigeWarteAufMieter(ticket.status);
  const mailZiel: NachrichtTyp | null = ziel === 'Mieter' || ziel === 'Eigentümer' ? ziel : null;
  const extern = mailZiel !== null;
  const mieterName = gewaehlt('Mieter')?.name ?? 'den Mieter';
  const platzhalter = ziel === 'intern'
    ? 'Interner Kommentar… (Enter = senden)'
    : ziel === 'portal'
      ? `Antwort an ${portalPerson?.name ?? 'niemanden'} – erscheint nur in deren Portal, es wird keine E-Mail verschickt`
    : ziel === 'Mieter'
      ? `Nachricht an ${mieterName}… wird im E-Mail-Fenster der Pille geöffnet`
      : 'Nachricht an den Eigentümer… wird im E-Mail-Fenster der Pille geöffnet';
  const feldFarbe = ziel === 'intern' ? 'var(--warn)' : ziel === 'portal' ? 'var(--ok)' : ziel === 'Mieter' ? 'var(--info)' : 'var(--acc)';
  const sendenAktiv = mailZiel ? !!gewaehlt(mailZiel) && mailLaedt === null : !!kommentarText.trim() && !kommentarSenden && (ziel !== 'portal' || !!portalPerson);

  const senden = () => {
    if (ziel === 'portal') { void handlePortalAntwort(); return; }
    if (!mailZiel) { void handleKommentarSenden(); return; }
    const k = gewaehlt(mailZiel);
    if (k) void handleNachrichtOeffnen(mailZiel, k.email, kommentarText.trim(), warteMoeglich && danachWarte);
  };

  return (
    <section aria-label="Antwort" className="immo-glas flex shrink-0 flex-col" style={{ borderRadius: 24, padding: 12, gap: 10 }}>
      <div className="flex flex-wrap items-center justify-between" style={{ gap: 8 }}>
        <Segment<Ziel> ariaLabel="Antwort an" hoehe={32} wert={ziel} onChange={setZiel} eintraege={[
          { key: 'intern', label: 'Intern', icon: ChatCircleText, iconFarbe: 'var(--warn)', tip: 'Interner Kommentar – nur für das Team sichtbar' },
          { key: 'Mieter', label: 'An Mieter', icon: User, iconFarbe: 'var(--info)', gesperrt: mieterListe.length === 0,
            tip: mieterListe.length === 0 ? grundText(mieterGesamt, 'Mieter') : `E-Mail an ${mieterName}` },
          { key: 'Eigentümer', label: 'An Eigentümer', icon: Buildings, iconFarbe: 'var(--acc)', gesperrt: eigentuemerListe.length === 0,
            tip: eigentuemerListe.length === 0 ? grundText(eigentuemerGesamt, 'Eigentümer') : 'E-Mail an den Eigentümer' },
          { key: 'portal', label: 'Im Portal', icon: Globe, iconFarbe: 'var(--ok)', gesperrt: portalPersonen.length === 0,
            tip: portalPersonen.length === 0 ? 'Niemand mit Portalzugang hat zu diesem Vorgang geschrieben' : 'Antwort im Mieter- bzw. Eigentümerportal – keine E-Mail' },
        ]} />
        {warteMoeglich && (
          <label className="inline-flex cursor-pointer items-center" style={{ gap: 7, fontSize: 12.5, fontWeight: 700, color: 'var(--ink2)' }}
            data-tip="Nach dem Öffnen der E-Mail den Status auf „Warte auf Mieter“ setzen">
            <input type="checkbox" checked={danachWarte} onChange={e => setDanachWarte(e.target.checked)} style={{ width: 15, height: 15, accentColor: 'var(--info)' }} />
            Danach „Warte auf Mieter“
          </label>
        )}
      </div>

      {ziel === 'portal' && portalPersonen.length > 0 && (
        <div className="flex flex-wrap items-center" style={{ gap: 6 }}>
          <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--ink3)' }}>An:</span>
          {portalPersonen.map(p => (
            <button key={p.profil} type="button" aria-pressed={portalAnProfil === p.profil} onClick={() => setPortalAnProfil(p.profil)}
              data-tip={`Antwort nur im Portal von ${p.name} (${PORTAL_TEXT[p.rolle]}) – niemand sonst sieht sie`}
              className="inline-flex items-center" style={{ height: 28, padding: '0 11px', borderRadius: 14, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700,
                background: portalAnProfil === p.profil ? 'var(--ok)' : 'var(--fill2)', color: portalAnProfil === p.profil ? '#fff' : 'var(--ink2)' }}>
              {p.name} · {PORTAL_TEXT[p.rolle]}
            </button>
          ))}
        </div>
      )}

      {mailZiel && listeVon(mailZiel).length > 1 && (
        <div className="flex flex-wrap items-center" style={{ gap: 6 }}>
          <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--ink3)' }}>An:</span>
          {listeVon(mailZiel).map(k => {
            const an = gewaehlt(mailZiel)?.id === k.id;
            return (
              <button key={k.id} type="button" aria-pressed={an} onClick={() => setEmpfaengerId(v => ({ ...v, [mailZiel]: k.id }))}
                data-tip={`Empfänger – ${k.name} auswählen`}
                className="inline-flex items-center" style={{ height: 28, padding: '0 11px', borderRadius: 14, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700,
                  background: an ? 'var(--info)' : 'var(--fill2)', color: an ? '#fff' : 'var(--ink2)' }}>
                {k.name}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex items-end" style={{ gap: 8 }}>
        {/* ListTextarea legt className aufs innere <textarea>; Breite über den äußeren flex-1-Wrapper */}
        <div className="min-w-0 flex-1">
          <ListTextarea glasTips
            value={kommentarText}
            onChange={(e) => setKommentarText(e.target.value)}
            rows={4}
            wachsenBis="min(45vh, 420px)"
            placeholder={platzhalter}
            aria-label={ziel === 'intern' ? 'Interner Kommentar' : ziel === 'portal' ? `Antwort im Portal an ${portalPerson ? PORTAL_TEXT[portalPerson.rolle] : '–'}` : `Nachricht an ${ziel}`}
            className="w-full resize-none"
            style={{ borderRadius: 18, padding: '12px 64px 12px 14px', border: 'none', outline: 'none', fontFamily: 'inherit', fontSize: 14, lineHeight: 1.45, color: 'var(--ink)',
              background: `color-mix(in srgb, ${feldFarbe} 9%, var(--fill2))`, boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${feldFarbe} 22%, transparent)` }}
            // Enter sendet nur intern; extern öffnet erst der Knopf das E-Mail-Fenster
            onKeyDown={(e) => { if (ziel === 'intern' && e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void handleKommentarSenden(); } }}
          />
        </div>
        <div className="flex shrink-0 flex-col items-center" style={{ gap: 8 }}>
          <div data-tip="KI-Textehelfer" className="inline-flex items-center justify-center" style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--fill2)' }}>
            <KiTextHelfer
              key={ticket.id}
              aktuellerText={kommentarText}
              kontext={{ ticketId: ticket.id }}
              onUebernehmen={setKommentarText}
            />
          </div>
          <button type="button" onClick={senden} disabled={!sendenAktiv}
            aria-label={extern ? 'Im E-Mail-Fenster öffnen' : ziel === 'portal' ? 'Antwort im Portal speichern' : 'Kommentar speichern'}
            data-tip={extern ? 'Im E-Mail-Fenster öffnen' : ziel === 'portal' ? 'Antwort im Portal speichern – keine E-Mail' : 'Kommentar speichern (Enter)'}
            className="inline-flex items-center justify-center"
            style={{ width: 44, height: 44, borderRadius: '50%', border: 'none', cursor: sendenAktiv ? 'pointer' : 'not-allowed',
              ...(sendenAktiv ? { background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)' } : { background: 'var(--fill2)', color: 'var(--ink3)' }) }}>
            {kommentarSenden || mailLaedt ? <SpinnerGap size={18} className="animate-spin" />
              : extern ? <EnvelopeSimpleOpen size={19} weight="fill" /> : <PaperPlaneTilt size={18} weight="fill" />}
          </button>
        </div>
      </div>
    </section>
  );
}
